import React, { useMemo } from 'react';
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form';
import { useFilterUsers } from '@/clients/users/useUsers.ts';
import { FilterOptionsUser } from '@/clients/users/types/users.ts';
import { SearchableCombobox } from '@/shared/ui/Combobox/SearchableCombobox.tsx';
import { withDefaultSearchableComboboxOption } from '@/shared/ui/Combobox/utils/withDefaultSearchableComboboxOption.ts';

import { useFormFieldSearch } from '@/shared/hooks/useFormFieldSearch.ts';

type UserIdFieldValues = {
    [key: string]: number | null;
};

export type SelectUserInputProps = Pick<UseControllerProps<UserIdFieldValues, string>, 'name' | 'defaultValue'> & {
    defaultValueLabel?: string;
    label: string;
    filters: Omit<FilterOptionsUser, 'search'>;
    disabled?: boolean;
    dataTest?: string;
    onValueChange?: (value: number | null) => void;
};

export const SelectUserInput = React.forwardRef<HTMLInputElement, SelectUserInputProps>(
    ({ name, defaultValue, defaultValueLabel, label, filters, disabled, dataTest, onValueChange }, _ref) => {
        const { control } = useFormContext();
        const { search, setSearch, debouncedSearch } = useFormFieldSearch(name);

        const { data, isFetching } = useFilterUsers({
            ...filters,
            search: debouncedSearch || undefined,
        });

        const options = useMemo(() => {
            const defaultOption =
                defaultValue && defaultValueLabel
                    ? {
                          value: String(defaultValue),
                          label: defaultValueLabel,
                      }
                    : undefined;

            return withDefaultSearchableComboboxOption({
                options: (data?.items ?? []).map(user => ({
                    value: String(user.id),
                    label: `${user.firstName} ${user.lastName}`,
                    description: user.username,
                })),
                defaultOption,
            });
        }, [data, defaultValue, defaultValueLabel]);

        return (
            <Controller
                name={name}
                defaultValue={defaultValue ?? null}
                control={control}
                render={({ field: { ref, onChange, value }, fieldState }) => (
                    <SearchableCombobox
                        fieldRef={ref}
                        label={label}
                        error={fieldState.error?.message}
                        search={search}
                        onSearchChange={setSearch}
                        loading={isFetching}
                        options={options}
                        value={value != null ? String(value) : undefined}
                        dataTest={dataTest}
                        onValueChange={userIdValue => {
                            const userId = userIdValue != null ? parseInt(userIdValue, 10) : null;

                            onChange(userId);
                            onValueChange?.(userId);
                        }}
                        disabled={disabled}
                    />
                )}
            />
        );
    }
);
